// Copyright (c) Docugami, Inc. All rights reserved.

import { ContextualMenu, DirectionalHint, Icon, IContextualMenuItem } from "@fluentui/react";
import { primaryFontExpanded } from "components/common/Fonts";
import { internalSettingsEnabled } from "components/settings/presentation/SettingsPanel";
import { primaryTheme } from "components/shell/presentation/AppStyles";
import { OrganizationNameSwitcher } from "components/shell/presentation/MainMenu";
import { useAccountView } from "hooks/AccountView";
import { useDeepLinkInitializer } from "hooks/deepLinkInitializer";
import { useDgAuth } from "hooks/DgAuthentication";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useOrgSwitcher } from "hooks/OrgSwitcher";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useUserProfileMenuItems } from "hooks/UserProfileMenuItems";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { setSettingsPanelShowing } from "store/settings/actions";
import styled from "styled-components";
import { SettingsPanelHiddenButtonToggledSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { useCurrentTheme } from "theming";
import { AutomationId } from "utils/Automation";
import { NavGroup, NavigationBar } from "./NavigationBar";

export enum RouteNavItem {
    Home = 'home',
    MyContent = 'my_content',
    Discovery = 'discovery',
    Understand = 'understand',
    Create = 'create',
    Workflow = 'workflow',
    Chat = 'Chat',
    Queries = 'Queries'
}

function getSelectedRouteItem(pathname: string) {
    if (pathname.startsWith('/home')) {
        return RouteNavItem.Home;
    } else if (pathname.startsWith('/dashboard')) {
        return RouteNavItem.Home;
    }

    switch (pathname) {
        case '/':
            return RouteNavItem.Home;
        case '/projects/understand':
            return RouteNavItem.Understand;
        case '/projects/create':
            return RouteNavItem.Create;
        case '/projects/workflow':
            return RouteNavItem.Workflow;
        case '/chat':
            return RouteNavItem.Chat;
        default:
            return '';
    }
}

const SmallText = styled.div`
    font-family: ${primaryFontExpanded};
    font-size: 16px;
    line-height: 17px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`;

const UsernameText = styled(SmallText)`
    font-size: 16px;
    font-weight: 700;
    line-height: 16px;
    padding-bottom: 5px;
    color: ${primaryTheme.palette.black};
`;

const UserDetailsRoot = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 15px;
`;

const ProfileIcon = styled.div`
    padding-right: 10px;
`;

const UsernameContainer = styled.div`
    flex: 1;
    width: 210px;
`;

export const GlobalNavigationBar = (): JSX.Element | null => {
    const location = useLocation();
    const [selectedKey, setSelectedKey] = useState(getSelectedRouteItem(location.pathname));
    const hidden = useAppSelector((state) => !state.display.navigationBarVisible);
    const settingsPanelShowing = useAppSelector((state) => state.settings.settingsPanelShowing);

    useDeepLinkInitializer();

    const contentNavDisabled = false;

    const [menuTarget, setMenuTarget] = useState<HTMLElement | null>(null);
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const menuItems = useUserProfileMenuItems();
    const orgSwitcher = useOrgSwitcher();
    const { user } = useDgAuth();
    const dispatch = useAppDispatch();
    const navService = useNavigationService();
    const dgIcons = useDgIcons();
    const currentTheme = useCurrentTheme();
    const { pagesCount, maxPages } = useAccountView();

    const [numClicks, setNumClicks] = useState(0);
    const [lastClicked, setLastClicked] = useState(Date.now());
    const [developerSettingsVisible, setDeveloperSettingsVisible] = useState(internalSettingsEnabled);

    useEffect(() => {
        if (numClicks >= 6) {
            Telemetry.trackEvent<SettingsPanelHiddenButtonToggledSchema>({
                eventName: TelemetryEvent.SettingsPanelHiddenButtonToggled
            });
            if (!developerSettingsVisible) {
                dispatch(setSettingsPanelShowing(true));
            }
            setDeveloperSettingsVisible(x => !x);
            setNumClicks(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numClicks]);

    const handleHiddenClick = useCallback(() => {
        const upDate = Date.now();
        if (upDate - lastClicked > 500) {
            setNumClicks(1);
        } else {
            setNumClicks(v => v + 1);
        }
        setLastClicked(upDate);
    }, [lastClicked])

    // Update selected key when location changes
    React.useEffect(() => {
        const currentPathId = getSelectedRouteItem(location.pathname);
        if (currentPathId !== selectedKey) {
            setSelectedKey(currentPathId);
        }
    }, [location.pathname, selectedKey]);

    const contextualMenuItems: IContextualMenuItem[] = useMemo(() => {
        const username = user?.name?.toUpperCase() ?? '';
        const userInfoItem: IContextualMenuItem = ({
            key: 'user_details',
            text: '',
            onRender: () => (
                <UserDetailsRoot key="user_details">
                    <ProfileIcon>
                        <Icon
                            iconName='ContactInfo'
                            styles={{ root: { fontSize: '30px', fontWeight: '300', margin: '0 5px' } }}
                        />
                    </ProfileIcon>
                    <UsernameContainer>
                        <UsernameText title={username}>{username}</UsernameText>
                        <OrganizationNameSwitcher orgSwitcherProps={{
                            ...orgSwitcher,
                            switchOrg: () => { orgSwitcher.switchOrg() }
                        }} />
                        {
                            pagesCount === undefined || !maxPages ? <></> :
                                <><br/><span><b>{pagesCount}</b> of <b>{maxPages}</b> Pages Consumed</span></>
                        }
                    </UsernameContainer>
                    {orgSwitcher.getOrgSwitcherDialog()}
                </UserDetailsRoot>
            )
        });

        const otherItems = menuItems.map<IContextualMenuItem>(item => ({
            key: item.title,
            text: item.title,
            onClick: item.onClick,
            iconProps: { iconName: !!item.icon ? '' : undefined },
            onRenderIcon: () => item.icon,
            subMenuProps: !!item.listItems.length ? {
                items: item.listItems.map(sub => ({
                    key: sub.key,
                    text: sub.text,
                    href: sub.url,
                    target: sub.target,
                    disabled: sub.disabled,
                    onClick: !!sub.onClick ? () => {
                        sub.onClick!();
                        setIsMenuVisible(false);
                    } : undefined,
                }))
            } : undefined
        }));

        return [userInfoItem, ...otherItems]
    }, [menuItems, orgSwitcher, user?.name, pagesCount, maxPages]);

    const navGroups: NavGroup[] = useMemo(() => {
        const disabledTooltip = contentNavDisabled ? `Please add more docs to use these features` : undefined;

        const globalNavGroup: NavGroup = {
            items: [
                {
                    key: RouteNavItem.Home,
                    name: 'Dashboard',
                    iconName: 'Home',
                    onClick: () => navService.navToHomePage(NavigationType.PushNewPage),
                    automationId: AutomationId.NavItemHome,
                    disabled: contentNavDisabled,
                    tooltip: disabledTooltip
                }
            ]
        };

        const pageNavGroup: NavGroup = {
            items: [
                {
                    key: RouteNavItem.Understand,
                    name: 'Reports',
                    iconName: 'Table',
                    onClick: () => navService.navToProjectsPage(NavigationType.PushNewPage, RouteNavItem.Understand),
                    automationId: AutomationId.NavItemProjects,
                    disabled: contentNavDisabled,
                    tooltip: disabledTooltip
                },
                {
                    key: RouteNavItem.Workflow,
                    name: 'Workflows',
                    iconName: 'Flow',
                    onClick: () => navService.navToProjectsPage(NavigationType.PushNewPage, RouteNavItem.Workflow),
                    automationId: AutomationId.NavItemProjects,
                    disabled: contentNavDisabled,
                    tooltip: disabledTooltip
                },
                {
                    key: RouteNavItem.Create,
                    name: 'Authoring',
                    iconName: 'PageEdit',
                    onClick: () => navService.navToProjectsPage(NavigationType.PushNewPage, RouteNavItem.Create),
                    automationId: AutomationId.NavItemProjects,
                    disabled: contentNavDisabled,
                    tooltip: disabledTooltip
                }
            ]
        };

        const toolsGroup: NavGroup = {
            items: [
                {
                    key: 'hidden_settings',
                    name: '',
                    iconName: '',
                    onClick: handleHiddenClick,
                    onRender: () => {
                        return <div style={{ width: "100%", height: "50px" }} onClick={handleHiddenClick} />
                    },
                    automationId: AutomationId.NavItemHidden
                }
            ]
        };

        if (!!developerSettingsVisible) {
            toolsGroup.items.push(
                {
                    key: 'settings',
                    name: 'Settings',
                    iconName: 'Settings',
                    onClick: () => dispatch(setSettingsPanelShowing(!settingsPanelShowing)),
                    automationId: AutomationId.NavItemSettings
                });
        }

        toolsGroup.items.push(...[
            {
                key: 'account',
                name: 'Account',
                iconName: 'Contact',
                onClick: (ev?: any) => {
                    setMenuTarget(ev?.currentTarget as HTMLElement);
                    setIsMenuVisible(v => !v);
                },
                automationId: AutomationId.NavItemAccount
            },
            {
                key: 'help',
                name: 'Help',
                iconName: 'Help',
                onClick: () => {
                    window.open('https://help.docugami.com', '_blank', 'noreferrer');
                },
                automationId: AutomationId.NavItemHelp
            },
            {
                key: 'feedback',
                name: 'Contact Support',
                iconName: 'Feedback',
                onClick: () => {
                    const userback = (window as any).Userback;
                    if (!!userback) {
                        userback.open();
                    }
                },
                automationId: AutomationId.NavItemFeedback
            }
        ])

        return [globalNavGroup, pageNavGroup, toolsGroup];
    }, [contentNavDisabled, navService, dispatch, settingsPanelShowing, developerSettingsVisible, handleHiddenClick]);

    if (hidden) {
        return null;
    }

    return (
        <>
            <NavigationBar
                groups={navGroups}
                selectedKey={selectedKey}
                header={{
                    icon: currentTheme.logo.LogoWithMarkComponent
                        ? <currentTheme.logo.LogoWithMarkComponent width={20} height={20} color={currentTheme.colors.headerText} />
                        : dgIcons.icon(dgIconNames.DocugamiLogo, currentTheme.brand.productName, 20, 20, currentTheme.colors.headerText, ''),
                    title: currentTheme.brand.productName.toUpperCase()
                }}
                themeColors={{
                    primary: currentTheme.colors.primary,
                    primaryDark: currentTheme.colors.primaryDark,
                    headerBackground: currentTheme.colors.headerBackground,
                    headerText: currentTheme.colors.headerText
                }}
            />
            <ContextualMenu
                items={contextualMenuItems}
                hidden={!isMenuVisible}
                target={menuTarget}
                onDismiss={() => setIsMenuVisible(false)}
                directionalHint={DirectionalHint.rightBottomEdge}
                isBeakVisible={false}
            />
        </>
    );
};
